import { Box, Center, Flex } from "styled-system/jsx";
import { noteCardRecipe } from "./noteCard.recipe";
import {
  Button,
  Icon,
  SimpleDialog,
  Spinner,
  Text,
  Tooltip,
} from "@ttc3k/trekker";
import { Archive, EditPencil } from "iconoir-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNoteCard } from "./useNoteCard";
import { css } from "styled-system/css";
import { DateInput } from "shared/components/atoms";
import { MIN_EVENT_DATE } from "shared/utils";
import { Mention, MentionsInput } from "react-mentions";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { NoteFormValues, useNoteCardForm } from "./useNoteCardForm";
import { FormProvider } from "react-hook-form";
import { NoteManyQuery } from "gql/generated";
import { LinkedTo } from "./LinkedTo";
import { useNotesContext } from "shared/contextProviders";

export type NoteCardProps = {
  initialMode: "view" | "edit";
  note?: NoteManyQuery["noteMany"][number];
  onCancel?: () => void;
  onSaveSuccess?: () => void;
  onArchiveSuccess?: () => void;
  onDeleteSuccess?: () => void;
  /** When true, card runs exit animation then calls onExitAnimationComplete (used by global notes for smooth delete). */
  isExiting?: boolean;
  /** If provided, delete is deferred: confirm calls this instead of deleting immediately; parent runs exit animation then deletes. */
  onDeleteRequested?: (note: NoteManyQuery["noteMany"][number]) => void;
  onExitAnimationComplete?: () => void;
  /** When set, card has fixed height and message area scrolls (e.g. for virtual lists). */
  messageMaxHeight?: number;
  size?: "sm" | "lg";
  style?: React.CSSProperties;
  className?: string;
  onCloseModal?: () => void;
  /** When set (e.g. to document.body), mentions dropdown is portaled here so it isn't clipped (e.g. in modals). */
  suggestionsPortalHost?: HTMLElement | null;
};

export const NoteCard = ({
  initialMode,
  note,
  onCancel,
  onSaveSuccess,
  onArchiveSuccess,
  onDeleteSuccess,
  isExiting = false,
  onDeleteRequested,
  onExitAnimationComplete,
  messageMaxHeight,
  size = "sm",
  style,
  className,
  onCloseModal,
  suggestionsPortalHost: suggestionsPortalHostProp,
}: NoteCardProps) => {
  const { t } = useTranslation("core", { keyPrefix: "NOTES" });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const { form, defaultValues, onSubmit } = useNoteCardForm({
    note,
    onSuccess: () => handleSaveSuccess(),
    onError: () => {},
  });

  const {
    mode,
    setMode,
    canEdit,
    allowArchiving,
    allowDeleting,
    createdBy,
    displayDate,
    hideDisplayDate,
    isSaving,
    mentionableUsers,
    message,
    noteDateISO,
    entityId,
    maxDate,
    handleEditClick,
    handleCancelClick,
    handleArchiveClick,
    isDeleteConfirmOpen,
    setIsDeleteConfirmOpen,
    handleDeleteConfirmClick,
    isDeleting,
  } = useNoteCard({
    initialMode,
    note,
    form,
    defaultValues: defaultValues as NoteFormValues,
    onCancel,
    onArchiveSuccess,
    onDeleteSuccess,
    onDeleteRequested,
  });

  const { defaultNoteEntity } = useNotesContext();

  const handleSaveSuccess = () => {
    onSaveSuccess?.();
    setMode("view");
  };

  const isArchived = !!note && !!note.isArchived;
  const isScopedEntity = !!defaultNoteEntity;

  // Focus the input when the mode is edit
  useEffect(() => {
    if (inputRef.current && mode === "edit") {
      // This is in a timeout because if the note is being shown because of a button click,
      // we need to wait for the click event to complete before focusing the input
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [mode, inputRef]);

  const {
    root,
    createdBy: createdBySlot,
    dateDisplay: dateSlot,
    archivedDisplay: archivedDisplaySlot,
  } = noteCardRecipe({
    visual:
      !note || mode === "edit"
        ? "editing"
        : isArchived
          ? "archived"
          : "viewing",
  });

  const cardContent = (
    <Flex
      className={root + (className ? ` ${className}` : "")}
      ref={containerRef}
      style={{
        width: "100%",
        minWidth: 0,
        ...style,
        ...(messageMaxHeight != null
          ? { height: messageMaxHeight, minHeight: messageMaxHeight, flexDirection: "column" as const }
          : {}),
      }}
      position="relative"
    >
          <Flex
            flexDir="column"
            gap="150"
            flex={messageMaxHeight != null ? "1 1 0" : undefined}
            minHeight={messageMaxHeight != null ? 0 : undefined}
          >
            {!isScopedEntity && (
              <LinkedTo
                mode={isArchived ? "archived" : mode}
                onNavigateAway={onCloseModal}
              />
            )}

            <Box
              flex={messageMaxHeight != null ? "1 1 0" : undefined}
              minHeight={messageMaxHeight != null ? 0 : undefined}
              overflowY={messageMaxHeight != null ? "auto" : undefined}
              css={{
                "& .mentions__highlighter": isArchived
                  ? {
                      opacity: 0.6, // When viewing an archived note, the text is grayed out
                    }
                  : {},
                "& .mentions--multiLine .mentions__highlighter":
                  size === "lg"
                    ? {
                        minHeight: "200px !important",
                        maxHeight: "200px !important",
                      }
                    : {}, // Adjust the size of the mentions input depending on the size of the note
              }}
            >
              {/* Note message input + mentions ... CSS is contained in /shared/styles/global.css */}
              <MentionsInput
                value={message}
                onChange={e =>
                  form.setValue("message", e.target.value, {
                    shouldDirty: true,
                  })
                }
                className={"mentions"}
                disabled={mode === "view" || isSaving || isArchived}
                autoFocus={mode === "edit"}
                suggestionsPortalHost={
                  suggestionsPortalHostProp ?? containerRef.current ?? undefined
                }
                allowSuggestionsAboveCursor={true}
                allowSpaceInQuery={true}
                inputRef={inputRef}
              >
                <Mention
                  trigger="@"
                  data={mentionableUsers}
                  markup={"**@[__display__](__id__)**"}
                  displayTransform={(_, display) => {
                    return "@" + display;
                  }}
                  appendSpaceOnAdd
                  onAdd={(id, display) => {
                    // If the mention is already in the list, don't add it again
                    const mentions = form.getValues("mentions");
                    if (mentions.some(m => m.id === id)) {
                      return;
                    }
                    form.setValue(
                      "mentions",
                      [...mentions, { id: id.toString(), display }],
                      { shouldDirty: true },
                    );
                  }}
                />
              </MentionsInput>
            </Box>
          </Flex>

          {/* Note footer */}
          <Flex
            alignItems="flex-end"
            justifyContent="space-between"
            gap="100"
            flex={messageMaxHeight != null ? "0 0 auto" : undefined}
          >
            <Flex flexDir="column">
              {/* View MODE to potentially display the createdBy name and noteDateISO */}
              {mode === "view" && createdBy && (
                <Text visual="smallSemiBold" className={createdBySlot}>
                  {createdBy}
                </Text>
              )}
              {mode === "view" && displayDate && !hideDisplayDate && (
                <Text visual="smallRegular" className={dateSlot}>
                  {displayDate}
                </Text>
              )}
              {/* Edit MODE to allow the user to select the noteDateISO */}
              {mode === "edit" && (
                <DateInput
                  max={maxDate}
                  min={MIN_EVENT_DATE}
                  canSelectPastDates={true}
                  value={noteDateISO ?? undefined}
                  onValueChange={value =>
                    form.setValue("noteDateISO", value, {
                      shouldDirty: true,
                    })
                  }
                  triggerProps={{
                    className: css({
                      py: noteDateISO ? "5px !important" : "100",
                    }),
                    disabled: isSaving,
                  }}
                  hideIcon
                />
              )}
            </Flex>
            <Flex
              justifyContent="flex-end"
              alignItems="center"
              gap="100"
              minH="32px"
            >
              {/* Edit MODE: Cancel, Delete, Archive, Save with subtle tap animation */}
              {mode === "edit" ? (
                <>
                  <motion.span
                    style={{ display: "inline-block" }}
                    whileTap={{ scale: 0.92, opacity: 0.9 }}
                    transition={{ duration: 0.1 }}
                  >
                    <Button
                      visual="unstyled"
                      size="sm"
                      color="text.light"
                      _hover={{ color: "text.mid" }}
                      _focus={{ color: "text.mid" }}
                      fontWeight="semiBold"
                      type="button"
                      disabled={isSaving}
                      onClick={handleCancelClick}
                    >
                      {t("ACTIONS.CANCEL")}
                    </Button>
                  </motion.span>
                  {note && allowDeleting && (
                    <motion.span
                      style={{ display: "inline-block" }}
                      whileTap={{ scale: 0.92, opacity: 0.9 }}
                      transition={{ duration: 0.1 }}
                    >
                      <Button
                        visual="unstyled"
                        size="sm"
                        color="text.error.mid"
                        _hover={{ color: "text.error.dark" }}
                        _focus={{ color: "text.error.dark" }}
                        fontWeight="semiBold"
                        type="button"
                        disabled={isSaving}
                        onClick={() => setIsDeleteConfirmOpen(true)}
                      >
                        {t("ACTIONS.DELETE")}
                      </Button>
                    </motion.span>
                  )}
                  {note && allowArchiving && (
                    <motion.span
                      style={{ display: "inline-block" }}
                      whileTap={{ scale: 0.92, opacity: 0.9 }}
                      transition={{ duration: 0.1 }}
                    >
                      <Button
                        visual="unstyled"
                        size="sm"
                        color="text.mid"
                        _hover={{ color: "text.dark" }}
                        _focus={{ color: "text.dark" }}
                        fontWeight="semiBold"
                        type="button"
                        disabled={isSaving}
                        onClick={handleArchiveClick}
                      >
                        {t("ACTIONS.ARCHIVE")}
                      </Button>
                    </motion.span>
                  )}
                  <motion.span
                    style={{ display: "inline-block" }}
                    whileTap={{ scale: 0.92, opacity: 0.9 }}
                    transition={{ duration: 0.1 }}
                  >
                    <Button
                      visual="unstyled"
                      size="sm"
                      color="text.accent.blue.mid"
                      _hover={{ color: "text.accent.blue.dark" }}
                      _focus={{ color: "text.accent.blue.dark" }}
                      fontWeight="semiBold"
                      type="submit"
                      disabled={
                        isSaving || !message.trim() || (entityId?.length ?? 0) === 0
                      }
                    >
                      {t("ACTIONS.SAVE")}
                    </Button>
                  </motion.span>
                </>
              ) : (
                <>
                  {isArchived ? (
                    <Flex className={archivedDisplaySlot}>
                      <Text visual="smallSemiBold">{t("STATUS.ARCHIVED")}</Text>
                      <Icon Element={Archive} />
                    </Flex>
                  ) : (
                    <>
                      {canEdit && (
                        <Tooltip content={t("ACTIONS.EDIT")}>
                          <motion.span
                            style={{ display: "inline-block" }}
                            whileTap={{ scale: 0.94 }}
                            transition={{ duration: 0.08 }}
                          >
                            <Button
                              visual="unstyled"
                              p="0"
                              Icon={EditPencil}
                              color="icon.highlight.mid"
                              _hover={{ color: "icon.highlight.dark" }}
                              _focus={{ color: "icon.highlight.dark" }}
                              type="button"
                              onClick={handleEditClick}
                            />
                          </motion.span>
                        </Tooltip>
                      )}
                    </>
                  )}
                </>
              )}
            </Flex>
          </Flex>
          {(isDeleting || (isSaving && mode === "edit")) && (
            <Center
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="0"
              bgColor="bg.dark/15"
              borderRadius="150"
            >
              <Spinner width="32px" height="32px" />
            </Center>
          )}
        </Flex>
  );

  const wrappedCard = isExiting ? (
    <motion.div
      initial={{ height: "auto", opacity: 1, marginBottom: 0 }}
      animate={{
        height: 0,
        opacity: 0,
        overflow: "hidden" as const,
        marginBottom: -12,
      }}
      transition={{ duration: 0.25, ease: "easeInOut" }}
      onAnimationComplete={() => {
        if (isExiting) onExitAnimationComplete?.();
      }}
    >
      {cardContent}
    </motion.div>
  ) : (
    cardContent
  );

  return (
    <FormProvider {...form}>
      <form id="note-card-form" onSubmit={onSubmit} style={{ width: "100%", minWidth: 0 }}>
        {wrappedCard}
      </form>

      {/* Delete confirm: portaled full-screen layer so it covers the entire global notes modal */}
      {isDeleteConfirmOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 2147483647,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SimpleDialog
              open={true}
              portal={false}
              title={t("DELETE_CONFIRM_MODAL.TITLE")}
              message={t("DELETE_CONFIRM_MODAL.DESCRIPTION")}
              cancelButtonCopy={t("ACTIONS.CANCEL")}
              onCancelClick={() => setIsDeleteConfirmOpen(false)}
              confirmButtonCopy={t("ACTIONS.DELETE")}
              confirmButtonVisual="destructive"
              onConfirmClick={handleDeleteConfirmClick}
            />
          </div>,
          document.body,
        )}
    </FormProvider>
  );
};
